// src/modulos/Reports/MaterialBudgetPage.js

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { db } from '../../firebaseConfig';
import { collection, getDocs } from 'firebase/firestore';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';
import { useAuxiliaryData } from '../../hooks/useAuxiliaryData';
import styles from './Reports.module.css';

// Icons
import {
    FiPlus, FiX, FiTrash2, FiDownload, FiFileText, FiPackage,
    FiSearch, FiRefreshCw, FiAlertCircle, FiCheck, FiPrinter,
    FiCalendar, FiUser, FiTruck, FiDollarSign, FiTrendingUp,
    FiClipboard, FiLayers, FiBox, FiEdit2, FiSave, FiChevronDown,
    FiChevronUp, FiExternalLink
} from 'react-icons/fi';
import { FaBox, FaBuilding, FaFileExcel, FaFilePdf, FaCalculator, FaClipboardList, FaTruckLoading } from 'react-icons/fa';

// Common Components
import { PageHeader, SearchBar, Toast, EmptyState, TableSkeleton, ConfirmModal } from '../../components/common';
import useToast from '../../hooks/useToast';

// ============================================================================
// CONSTANTS
// ============================================================================
const PRODUCTS_COLLECTION = 'products';
const RAW_MATERIALS_COLLECTION = 'raw-materials';
const SUPPLIERS_COLLECTION = 'suppliers';
const UNITS_COLLECTION = 'parameters/data/unidades-medida';
const NCM_COLLECTION = 'parameters/data/ncm';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if a material is packaging (unit-based) or raw material (mass-based)
 */
const isPackagingMaterial = (material, allCategories, allProductTypes) => {
    if (!material) return false;

    const materialCategories = material.categoryIds?.map(catId =>
        allCategories?.find(c => c.id === catId)?.name?.toUpperCase() || ''
    ) || [];

    const materialTypes = material.productTypeIds?.map(typeId =>
        allProductTypes?.find(t => t.id === typeId)?.name?.toUpperCase() || ''
    ) || [];

    const isProduct = materialCategories.some(name => name.includes('ACABADO')) ||
        materialTypes.some(name => name.includes('ACABADO'));

    if (isProduct) return false;

    const packagingTerms = ['EMBALAGEM', 'SERIGRAFIA', 'FRASCO', 'TAMPA', 'ROTULO', 'CAIXA', 'CARTUCHO'];

    return materialCategories.some(name => packagingTerms.some(term => name.includes(term))) ||
        materialTypes.some(name => packagingTerms.some(term => name.includes(term)));
};

/**
 * Check if a material is water (infinite resource)
 */
const isWaterMaterial = (material) => {
    if (!material) return false;
    const name = (material.commercialName || material.inciName || '').toUpperCase();
    return name.includes('AGUA') || name.includes('ÁGUA') || name === 'WATER';
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const MaterialBudgetPage = () => {
    const { t } = useTranslation();
    const can = usePagePermissions('relatorios_materiais');
    const { toasts, addToast, removeToast } = useToast();

    // Data hooks
    const { data: products, loading: productsLoading } = useFirestore(PRODUCTS_COLLECTION);
    const { data: allRawMaterials, loading: rawMaterialsLoading } = useFirestore(RAW_MATERIALS_COLLECTION);
    const { data: allSuppliers, loading: suppliersLoading } = useFirestore(SUPPLIERS_COLLECTION);
    const { data: allUnits, loading: unitsLoading } = useFirestore(UNITS_COLLECTION);
    const { data: allNcms, loading: ncmsLoading } = useFirestore(NCM_COLLECTION);
    const {
        allCategories,
        allProductTypes,
        isLoading: auxLoading
    } = useAuxiliaryData();

    // State
    const [selectedItems, setSelectedItems] = useState([]); // { productId, quantity, mode, tempId }
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [inputQuantity, setInputQuantity] = useState('');
    const [inputMode, setInputMode] = useState('mass'); // 'mass' or 'units'
    const [budgetName, setBudgetName] = useState('');
    const [budgetNotes, setBudgetNotes] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [showConfirmClear, setShowConfirmClear] = useState(false);
    const [expandedMaterials, setExpandedMaterials] = useState({});
    const [showAllSuppliers, setShowAllSuppliers] = useState({});

    const isLoading = productsLoading || rawMaterialsLoading || suppliersLoading || auxLoading || unitsLoading || ncmsLoading;

    // ============================================================================
    // HELPERS
    // ============================================================================
    
    /**
     * Get unit name from unit ID
     */
    const getUnitName = useCallback((unitId) => {
        if (!unitId) return '';
        const unit = allUnits?.find(u => u.id === unitId);
        return unit?.name || unitId;
    }, [allUnits]);

    /**
     * Get NCM code from NCM ID
     */
    const getNcmCode = useCallback((ncmId) => {
        if (!ncmId) return '';
        const ncm = allNcms?.find(n => n.id === ncmId);
        return ncm?.ncmCode || ncmId;
    }, [allNcms]);

    /**
     * Get supplier details by ID
     */
    const getSupplierDetails = useCallback((supplierId) => {
        if (!supplierId) return null;
        return allSuppliers?.find(s => s.id === supplierId) || null;
    }, [allSuppliers]);

    // ============================================================================
    // PRODUCT OPTIONS
    // ============================================================================
    const productOptions = useMemo(() => {
        if (!products) return [];
        return products
            .filter(p => p.situation !== 'Inativo')
            .map(p => ({
                value: p.id,
                label: `${p.code} - ${p.commercialName}`,
                product: p
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [products]);

    // ============================================================================
    // ADD ITEM TO BUDGET
    // ============================================================================
    const handleAddItem = useCallback(() => {
        if (!selectedProduct || !inputQuantity) return;

        const qty = parseFloat(inputQuantity);
        if (isNaN(qty) || qty <= 0) {
            addToast(t('pages.materialBudget.validation.invalidQuantity'), 'warning');
            return;
        }

        const product = selectedProduct.product;

        setSelectedItems(prev => [...prev, {
            tempId: Date.now() + Math.random(),
            productId: product.id,
            productCode: product.code,
            productName: product.commercialName,
            quantity: qty,
            mode: inputMode,
            netWeight: parseFloat(product.netWeight) || 0,
            gramatura: parseFloat(product.technical?.gramatura) || 0,
            gramaturaUnit: product.technical?.gramaturaUnit || 'g',
            rawMaterials: product.rawMaterials || [],
            productData: product
        }]);

        setSelectedProduct(null);
        setInputQuantity('');
    }, [selectedProduct, inputQuantity, inputMode, addToast, t]);

    const handleRemoveItem = useCallback((tempId) => {
        setSelectedItems(prev => prev.filter(item => item.tempId !== tempId));
    }, []);

    const handleClearAll = useCallback(() => {
        setSelectedItems([]);
        setBudgetName('');
        setBudgetNotes('');
        setShowConfirmClear(false);
    }, []);

    const toggleMaterialExpanded = useCallback((materialId) => {
        setExpandedMaterials(prev => ({
            ...prev,
            [materialId]: !prev[materialId]
        }));
    }, []);

    const toggleAllSuppliers = useCallback((materialId) => {
        setShowAllSuppliers(prev => ({
            ...prev,
            [materialId]: !prev[materialId]
        }));
    }, []);

    // ============================================================================
    // CALCULATE MATERIAL REQUIREMENTS
    // ============================================================================
    const materialRequirements = useMemo(() => {
        if (!selectedItems.length || !allRawMaterials) return [];

        const requirementsMap = {};

        selectedItems.forEach(item => {
            const netWeight = item.netWeight || 0;
            const gramatura = item.gramatura || 0;
            const gramaturaBase = (item.gramaturaUnit === 'g' || item.gramaturaUnit === 'ml')
                ? gramatura / 1000
                : gramatura;

            // Calculate total mass based on mode
            const totalMass = item.mode === 'units'
                ? item.quantity * gramaturaBase
                : item.quantity;

            // Process each raw material in the product formula
            (item.rawMaterials || []).forEach(rm => {
                if (!rm.id) return;

                const materialData = allRawMaterials.find(m => m.id === rm.id);
                if (!materialData) return;

                const isPackaging = isPackagingMaterial(materialData, allCategories, allProductTypes);
                const isWater = isWaterMaterial(materialData);
                const quantityInFormula = parseFloat(rm.quantity) || 0;

                let neededQuantity = 0;

                if (isPackaging) {
                    // Unit-based calculation: quantity per unit * number of units
                    neededQuantity = item.quantity * quantityInFormula;
                } else {
                    // Mass-based calculation: (total mass * percentage / 100)
                    neededQuantity = (totalMass * quantityInFormula) / 100;
                }

                // Aggregate by material ID
                if (!requirementsMap[rm.id]) {
                    // Get ALL suppliers with full details
                    const supplierRelations = (materialData.suppliers || []).map(s => {
                        const supplierDetails = getSupplierDetails(s.supplierId);
                        return {
                            supplierId: s.supplierId,
                            razaoSocial: s.razaoSocial,
                            codigo: s.codigo,
                            unitPrice: parseFloat(s.valor) || 0,
                            unitOfMeasureId: s.unitOfMeasure || '',
                            unitOfMeasureName: getUnitName(s.unitOfMeasure),
                            origemIcms: s.origemIcms,
                            leadTime: parseInt(s.leadTime) || 0,
                            loteMinimo: parseFloat(s.loteMinimo) || 0,
                            data: s.data,
                            descricao: s.descricao,
                            // Extra details from supplier collection
                            cnpjCpf: supplierDetails?.cnpjCpf || '',
                            email: supplierDetails?.email || '',
                            // Get first contact phone from addresses if available
                            phone: supplierDetails?.addresses?.[0]?.contacts?.[0]?.phone || 
                                   supplierDetails?.phone || '',
                            contactName: supplierDetails?.addresses?.[0]?.contacts?.[0]?.name || '',
                            contactEmail: supplierDetails?.addresses?.[0]?.contacts?.[0]?.email || ''
                        };
                    }).sort((a, b) => {
                        // Sort by data (most recent first), then price
                        if (a.data && b.data) return b.data.localeCompare(a.data);
                        return a.unitPrice - b.unitPrice;
                    });

                    // Resolve the unit properly
                    const resolvedUnit = isPackaging 
                        ? 'Un' 
                        : getUnitName(materialData.unidadeMedidaInterna) || 'Kg';

                    // Get NCM code
                    const ncmCode = getNcmCode(materialData.ncm);

                    requirementsMap[rm.id] = {
                        materialId: rm.id,
                        materialCode: materialData.code || '',
                        materialName: materialData.commercialName || materialData.inciName || 'N/A',
                        inciName: materialData.inciName || '',
                        inciNamePt: materialData.inciNamePt || '',
                        casNumber: materialData.casNumber || '',
                        ncmCode,
                        ncmId: materialData.ncm || '',
                        isPackaging,
                        isWater,
                        unit: resolvedUnit,
                        unitId: materialData.unidadeMedidaInterna || '',
                        totalNeeded: 0,
                        custoCompra: parseFloat(materialData.custoCompra) || 0,
                        suppliers: supplierRelations,
                        usedInProducts: [],
                        // For pricing, use the most recent supplier price
                        latestPrice: supplierRelations.length > 0 ? supplierRelations[0].unitPrice : (parseFloat(materialData.custoCompra) || 0),
                        latestSupplier: supplierRelations.length > 0 ? supplierRelations[0].razaoSocial : 'N/A',
                        preferredSupplier: supplierRelations.length > 0 ? supplierRelations[0] : null,
                        suppliersCount: supplierRelations.length
                    };
                }

                // Add quantity
                if (!isWater) {
                    requirementsMap[rm.id].totalNeeded += neededQuantity;
                }

                // Track which products use this material and the quantity per product
                const existingProduct = requirementsMap[rm.id].usedInProducts.find(
                    p => p.productId === item.productId
                );
                if (existingProduct) {
                    existingProduct.quantity += neededQuantity;
                } else {
                    requirementsMap[rm.id].usedInProducts.push({
                        productId: item.productId,
                        productCode: item.productCode,
                        productName: item.productName,
                        quantity: neededQuantity,
                        mode: item.mode,
                        // Store the calculation basis for transparency
                        productQuantity: item.quantity,
                        productMode: item.mode,
                        formulaPercentage: quantityInFormula
                    });
                }
            });
        });

        // Convert to array and sort
        return Object.values(requirementsMap)
            .filter(r => !r.isWater)
            .sort((a, b) => {
                if (a.isPackaging !== b.isPackaging) return a.isPackaging ? -1 : 1;
                return a.materialName.localeCompare(b.materialName);
            });
    }, [selectedItems, allRawMaterials, allCategories, allProductTypes, getSupplierDetails, getUnitName, getNcmCode]);

    // ============================================================================
    // CALCULATE TOTALS
    // ============================================================================
    const totals = useMemo(() => {
        const totalItems = materialRequirements.length;
        const totalCost = materialRequirements.reduce((sum, item) => {
            return sum + (item.totalNeeded * item.latestPrice);
        }, 0);

        const packagingItems = materialRequirements.filter(r => r.isPackaging).length;
        const rawMaterialItems = materialRequirements.filter(r => !r.isPackaging).length;

        return {
            totalItems,
            packagingItems,
            rawMaterialItems,
            totalCost,
            totalProducts: selectedItems.length,
            totalQuantity: selectedItems.reduce((sum, item) => sum + item.quantity, 0)
        };
    }, [materialRequirements, selectedItems]);

    // ============================================================================
    // EXPORT FUNCTIONS
    // ============================================================================
    const handleExportExcel = useCallback(() => {
        if (materialRequirements.length === 0) {
            addToast(t('pages.materialBudget.noDataToExport'), 'warning');
            return;
        }

        try {
            const wb = XLSX.utils.book_new();

            // === Sheet 1: Resumo de Materiais ===
            const summaryData = materialRequirements.map((req, index) => ({
                '#': index + 1,
                'Código': req.materialCode,
                'Material': req.materialName,
                'INCI': req.inciName,
                'INCI (PT)': req.inciNamePt,
                'CAS Number': req.casNumber,
                'NCM': req.ncmCode,
                'Tipo': req.isPackaging ? 'Embalagem' : 'Matéria-Prima',
                'Unidade': req.unit,
                'Quantidade Necessária': parseFloat(req.totalNeeded.toFixed(4)),
                'Preço Unitário': req.latestPrice,
                'Custo Total': parseFloat((req.totalNeeded * req.latestPrice).toFixed(2)),
                'Fornecedores': req.suppliersCount,
                'Fornecedor Preferencial': req.latestSupplier,
                'Lead Time (dias)': req.preferredSupplier?.leadTime || 0,
                'Lote Mínimo': req.preferredSupplier?.loteMinimo || 0
            }));

            const wsSummary = XLSX.utils.json_to_sheet(summaryData);
            wsSummary['!cols'] = [
                { wch: 5 }, { wch: 12 }, { wch: 30 }, { wch: 25 }, { wch: 25 }, { wch: 15 },
                { wch: 12 }, { wch: 15 }, { wch: 10 }, { wch: 18 }, { wch: 12 }, { wch: 12 },
                { wch: 12 }, { wch: 25 }, { wch: 12 }, { wch: 12 }
            ];
            XLSX.utils.book_append_sheet(wb, wsSummary, 'Materiais Necessários');

            // === Sheet 2: Por Fornecedor (TODOS os fornecedores) ===
            const supplierMap = {};
            materialRequirements.forEach(req => {
                req.suppliers.forEach(sup => {
                    if (!supplierMap[sup.supplierId]) {
                        supplierMap[sup.supplierId] = {
                            supplierId: sup.supplierId,
                            razaoSocial: sup.razaoSocial,
                            codigo: sup.codigo,
                            cnpjCpf: sup.cnpjCpf,
                            email: sup.email,
                            phone: sup.phone,
                            contactName: sup.contactName,
                            contactEmail: sup.contactEmail,
                            items: [],
                            total: 0,
                            maxLeadTime: 0
                        };
                    }
                    supplierMap[sup.supplierId].items.push({
                        materialCode: req.materialCode,
                        materialName: req.materialName,
                        ncmCode: req.ncmCode,
                        quantity: req.totalNeeded,
                        unit: req.unit,
                        unitPrice: sup.unitPrice,
                        total: req.totalNeeded * sup.unitPrice,
                        leadTime: sup.leadTime,
                        loteMinimo: sup.loteMinimo,
                        data: sup.data,
                        isPreferred: req.preferredSupplier?.supplierId === sup.supplierId
                    });
                    supplierMap[sup.supplierId].total += req.totalNeeded * sup.unitPrice;
                    supplierMap[sup.supplierId].maxLeadTime = Math.max(
                        supplierMap[sup.supplierId].maxLeadTime,
                        sup.leadTime || 0
                    );
                });
            });

            const supplierData = Object.values(supplierMap).map(sup => ({
                'Código Fornecedor': sup.codigo,
                'Fornecedor': sup.razaoSocial,
                'CNPJ/CPF': sup.cnpjCpf,
                'E-mail': sup.email || sup.contactEmail,
                'Telefone': sup.phone,
                'Contato': sup.contactName,
                'Qtd. Materiais': sup.items.length,
                'Materiais': sup.items.map(i => `${i.materialCode} - ${i.materialName}${i.isPreferred ? ' (Preferencial)' : ''}`).join('; '),
                'NCMs': sup.items.map(i => i.ncmCode).filter(Boolean).join('; '),
                'Quantidades': sup.items.map(i => `${i.quantity.toFixed(3)} ${i.unit}`).join('; '),
                'Valor Total': parseFloat(sup.items.reduce((sum, i) => sum + i.total, 0).toFixed(2)),
                'Lead Time Máx (dias)': sup.maxLeadTime
            }));

            const wsSuppliers = XLSX.utils.json_to_sheet(supplierData);
            wsSuppliers['!cols'] = [
                { wch: 12 }, { wch: 30 }, { wch: 20 }, { wch: 25 }, { wch: 18 },
                { wch: 20 }, { wch: 12 }, { wch: 50 }, { wch: 20 }, { wch: 30 },
                { wch: 15 }, { wch: 15 }
            ];
            XLSX.utils.book_append_sheet(wb, wsSuppliers, 'Por Fornecedor');

            // === Sheet 3: Produtos Selecionados ===
            const productsData = selectedItems.map((item, index) => ({
                '#': index + 1,
                'Código': item.productCode,
                'Produto': item.productName,
                'Quantidade': item.quantity,
                'Modo': item.mode === 'mass' ? 'Massa (Kg)' : 'Unidades',
                'Peso Líquido Unit.': item.netWeight,
                'Gramatura': item.gramatura,
                'Unidade Gramatura': item.gramaturaUnit
            }));

            const wsProducts = XLSX.utils.json_to_sheet(productsData);
            wsProducts['!cols'] = [
                { wch: 5 }, { wch: 12 }, { wch: 30 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 15 }
            ];
            XLSX.utils.book_append_sheet(wb, wsProducts, 'Produtos');

            // === Sheet 4: Detalhamento por Produto (quantidade de cada material por produto) ===
            const detailData = [];
            selectedItems.forEach(item => {
                materialRequirements.forEach(req => {
                    const productUsage = req.usedInProducts.find(p => p.productId === item.productId);
                    if (productUsage) {
                        detailData.push({
                            'Produto': `${item.productCode} - ${item.productName}`,
                            'Qtd. Produto': item.quantity,
                            'Modo Produto': item.mode === 'mass' ? 'Massa (Kg)' : 'Unidades',
                            'Material': `${req.materialCode} - ${req.materialName}`,
                            'INCI': req.inciName,
                            'NCM': req.ncmCode,
                            'Tipo Material': req.isPackaging ? 'Embalagem' : 'Matéria-Prima',
                            '% na Fórmula': productUsage.formulaPercentage,
                            'Quantidade Necessária': parseFloat(productUsage.quantity.toFixed(4)),
                            'Unidade': req.unit
                        });
                    }
                });
            });

            if (detailData.length > 0) {
                const wsDetail = XLSX.utils.json_to_sheet(detailData);
                wsDetail['!cols'] = [
                    { wch: 30 }, { wch: 12 }, { wch: 15 }, { wch: 35 }, { wch: 25 }, { wch: 12 },
                    { wch: 15 }, { wch: 12 }, { wch: 20 }, { wch: 10 }
                ];
                XLSX.utils.book_append_sheet(wb, wsDetail, 'Detalhamento por Produto');
            }

            // === Sheet 5: Todos os Fornecedores por Material ===
            const allSuppliersData = [];
            materialRequirements.forEach(req => {
                req.suppliers.forEach(sup => {
                    allSuppliersData.push({
                        'Material': `${req.materialCode} - ${req.materialName}`,
                        'NCM': req.ncmCode,
                        'Qtd. Necessária': parseFloat(req.totalNeeded.toFixed(4)),
                        'Unidade': req.unit,
                        'Fornecedor': sup.razaoSocial,
                        'Código Fornecedor': sup.codigo,
                        'CNPJ/CPF': sup.cnpjCpf,
                        'Preço Unitário': sup.unitPrice,
                        'Valor Total': parseFloat((req.totalNeeded * sup.unitPrice).toFixed(2)),
                        'U.M. Fornecedor': sup.unitOfMeasureName,
                        'Lead Time (dias)': sup.leadTime,
                        'Lote Mínimo': sup.loteMinimo,
                        'Data Cotação': sup.data,
                        'Preferencial': req.preferredSupplier?.supplierId === sup.supplierId ? 'Sim' : 'Não',
                        'Observações': sup.descricao
                    });
                });
            });

            if (allSuppliersData.length > 0) {
                const wsAllSuppliers = XLSX.utils.json_to_sheet(allSuppliersData);
                wsAllSuppliers['!cols'] = [
                    { wch: 35 }, { wch: 12 }, { wch: 15 }, { wch: 10 }, { wch: 30 }, { wch: 12 },
                    { wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 12 }, { wch: 12 },
                    { wch: 12 }, { wch: 12 }, { wch: 30 }
                ];
                XLSX.utils.book_append_sheet(wb, wsAllSuppliers, 'Todos Fornecedores');
            }

            // Save
            const fileName = `Orcamento_Materiais_${new Date().toISOString().split('T')[0]}.xlsx`;
            XLSX.writeFile(wb, fileName);

            addToast(t('pages.materialBudget.exportSuccess'), 'success');
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            addToast(t('pages.materialBudget.exportError'), 'error');
        }
    }, [materialRequirements, selectedItems, addToast, t]);

    // ============================================================================
    // PRINT REPORT
    // ============================================================================
    const handlePrint = useCallback(() => {
    if (materialRequirements.length === 0) {
        addToast(t('pages.materialBudget.noDataToPrint'), 'warning');
        return;
    }

    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (!printWindow) {
        addToast('Por favor, permita pop-ups para imprimir.', 'warning');
        return;
    }

    // ---- formatters (same as invoice) ----
    const formatNumber = (value, decimals = 3) => {
        if (!value && value !== 0) return '0';
        return Number(value).toLocaleString('pt-BR', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    };
    const formatCurrencyBRL = (value) => {
        if (!value && value !== 0) return 'R$ -';
        return `R$ ${Number(value).toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };
    const formatCurrentDateTime = () => {
        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        return `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
    };

    const today = new Date();
    const formattedDate = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;

    // ---- pagination ----
    const ROWS_PER_PAGE = 15;
    const materialChunks = [];
    for (let i = 0; i < materialRequirements.length; i += ROWS_PER_PAGE) {
        materialChunks.push(materialRequirements.slice(i, i + ROWS_PER_PAGE));
    }
    if (materialChunks.length === 0) materialChunks.push([]);

    // ---- group suppliers (all suppliers) ----
    const supplierMap = {};
    materialRequirements.forEach(req => {
        req.suppliers.forEach(sup => {
            if (!supplierMap[sup.supplierId]) {
                supplierMap[sup.supplierId] = {
                    supplierId: sup.supplierId,
                    razaoSocial: sup.razaoSocial,
                    codigo: sup.codigo,
                    cnpjCpf: sup.cnpjCpf,
                    email: sup.email || sup.contactEmail,
                    phone: sup.phone,
                    contactName: sup.contactName,
                    items: [],
                    total: 0,
                    maxLeadTime: 0
                };
            }
            supplierMap[sup.supplierId].items.push({
                materialCode: req.materialCode,
                materialName: req.materialName,
                unit: req.unit,
                quantity: req.totalNeeded,
                unitPrice: sup.unitPrice,
                isPreferred: req.preferredSupplier?.supplierId === sup.supplierId
            });
            supplierMap[sup.supplierId].total += req.totalNeeded * sup.unitPrice;
            supplierMap[sup.supplierId].maxLeadTime = Math.max(
                supplierMap[sup.supplierId].maxLeadTime,
                sup.leadTime || 0
            );
        });
    });
    const supplierList = Object.values(supplierMap).sort((a, b) => b.total - a.total);

    // ---- render helpers ----
    const renderTableHead = () => `
        <thead>
            <tr style="background:#e8eaf6;">
                <th style="border:1px solid #ddd; padding:6px; text-align:center; font-weight:bold; width:35px;">#</th>
                <th style="border:1px solid #ddd; padding:6px; text-align:left; font-weight:bold;">MATERIAL</th>
                <th style="border:1px solid #ddd; padding:6px; text-align:center; font-weight:bold; width:70px;">NCM</th>
                <th style="border:1px solid #ddd; padding:6px; text-align:center; font-weight:bold; width:70px;">TIPO</th>
                <th style="border:1px solid #ddd; padding:6px; text-align:center; font-weight:bold; width:90px;">QTD. NEC.</th>
                <th style="border:1px solid #ddd; padding:6px; text-align:center; font-weight:bold; width:45px;">UN.</th>
                <th style="border:1px solid #ddd; padding:6px; text-align:center; font-weight:bold; width:80px;">PREÇO UNIT.</th>
                <th style="border:1px solid #ddd; padding:6px; text-align:center; font-weight:bold; width:90px;">CUSTO TOTAL</th>
                <th style="border:1px solid #ddd; padding:6px; text-align:center; font-weight:bold; width:140px;">FORNECEDOR PREF.</th>
                <th style="border:1px solid #ddd; padding:6px; text-align:center; font-weight:bold; width:60px;">LEAD</th>
            </tr>
        </thead>
    `;

    const renderItemRow = (req, globalIdx) => `
        <tr style="${globalIdx % 2 === 1 ? 'background:#fafafa;' : ''}">
            <td style="border:1px solid #ddd; padding:6px; text-align:center;">${globalIdx + 1}</td>
            <td style="border:1px solid #ddd; padding:6px; font-size:9px; line-height:1.3;">
                <div><strong>${req.materialCode}</strong> - ${req.materialName}</div>
                ${req.inciName && req.inciName !== req.materialName ? `<div style="font-size:8px; color:#666;">INCI: ${req.inciName}</div>` : ''}
                ${req.casNumber ? `<div style="font-size:8px; color:#666;">CAS: ${req.casNumber}</div>` : ''}
            </td>
            <td style="border:1px solid #ddd; padding:6px; text-align:center; font-family:monospace; font-size:8px;">${req.ncmCode || '—'}</td>
            <td style="border:1px solid #ddd; padding:6px; text-align:center;">
                <span style="padding:1px 6px; border-radius:10px; font-size:8px; background:${req.isPackaging ? '#e3f2fd' : '#f3e5f5'}; color:${req.isPackaging ? '#1565c0' : '#7b1fa2'};">
                    ${req.isPackaging ? 'EMB' : 'MP'}
                </span>
            </td>
            <td style="border:1px solid #ddd; padding:6px; text-align:right; font-weight:bold; color:#1a5276;">${formatNumber(req.totalNeeded, 3)}</td>
            <td style="border:1px solid #ddd; padding:6px; text-align:center;">${req.unit}</td>
            <td style="border:1px solid #ddd; padding:6px; text-align:right;">${formatCurrencyBRL(req.latestPrice)}</td>
            <td style="border:1px solid #ddd; padding:6px; text-align:right; font-weight:bold; color:#e67e22;">${formatCurrencyBRL(req.totalNeeded * req.latestPrice)}</td>
            <td style="border:1px solid #ddd; padding:6px; font-size:8px;">
                ${req.latestSupplier || '—'}
                ${req.suppliersCount > 1 ? `<div style="font-size:7px; color:#666;">+${req.suppliersCount - 1} fornecedor(es)</div>` : ''}
            </td>
            <td style="border:1px solid #ddd; padding:6px; text-align:center;">${req.preferredSupplier?.leadTime ? `${req.preferredSupplier.leadTime}d` : '—'}</td>
        </tr>
    `;

    const renderTotalsRows = () => `
        <tr style="font-weight:bold; background:#f5f5f5;">
            <td colspan="7" style="border:1px solid #ddd; padding:6px; text-align:right;">SUBTOTAL MATERIAIS</td>
            <td style="border:1px solid #ddd; padding:6px; text-align:right;">${formatCurrencyBRL(totals.totalCost)}</td>
            <td colspan="2" style="border:1px solid #ddd; padding:6px;"></td>
        </tr>
        <tr style="font-weight:bold; background:#e8eaf6;">
            <td colspan="7" style="border:1px solid #ddd; padding:6px; text-align:right; font-size:11px;">TOTAL GERAL (${materialRequirements.length} materiais)</td>
            <td style="border:1px solid #ddd; padding:6px; text-align:right; font-size:11px;">${formatCurrencyBRL(totals.totalCost)}</td>
            <td colspan="2" style="border:1px solid #ddd; padding:6px;"></td>
        </tr>
    `;

    // ---- page builder ----
    const buildPage = (chunk, pageIndex, isFirst, isLast) => {
        const chunkHtml = chunk.map((req, idx) =>
            renderItemRow(req, pageIndex * ROWS_PER_PAGE + idx)
        ).join('');

        return `
        <div class="page-block">
            ${isFirst ? `
                <!-- HEADER -->
                <div style="display:flex; justify-content:space-between; align-items:center; border-bottom:2px solid #1a237e; padding-bottom:12px; margin-bottom:12px;">
                    <div style="width:200px; height:60px; display:flex; align-items:center; justify-content:center; border:1px solid #ccc; border-radius:4px; padding:6px; background:#fff;">
                        <span style="font-size:12px; color:#666; font-weight:bold;">LOGO</span>
                    </div>
                    <div style="text-align:center;">
                        <div style="font-size:26px; font-weight:bold; color:#1a237e; letter-spacing:2px;">ORÇAMENTO DE MATERIAIS</div>
                        <div style="font-size:11px; font-weight:bold; color:#333; margin-top:4px;">Ref: ${budgetName || 'Sem título'}</div>
                        <div style="font-size:11px; font-weight:bold; color:#333; margin-top:4px;">DATA: ${formattedDate}</div>
                    </div>
                    <div style="text-align:left;">
                        <div style="font-size:9px; color:#666;">PÁG.</div>
                        <div style="font-size:9px; color:#666;">${pageIndex + 1} / ${materialChunks.length}</div>
                    </div>
                </div>

                <!-- INFO GRID -->
                <div style="display:grid; grid-template-columns:1fr 1fr 1fr; gap:15px; margin-bottom:12px; border:1px solid #ddd; padding:12px; background:#fafafa;">
                    <div>
                        <div style="font-weight:bold; font-size:8px; color:#666;">1. ORÇAMENTO:</div>
                        <div style="font-size:9px; font-weight:bold;">${budgetName || 'Sem título'}</div>
                    </div>
                    <div>
                        <div style="font-weight:bold; font-size:8px; color:#666;">2. DATA DE GERAÇÃO:</div>
                        <div style="font-size:9px;">${formatCurrentDateTime()}</div>
                    </div>
                    <div>
                        <div style="font-weight:bold; font-size:8px; color:#666;">3. RESPONSÁVEL:</div>
                        <div style="font-size:9px;">${t('pages.materialBudget.systemUser') || 'Sistema'}</div>
                    </div>
                    <div>
                        <div style="font-weight:bold; font-size:8px; color:#666;">4. PRODUTOS SELECIONADOS:</div>
                        <div style="font-size:9px;">${selectedItems.length}</div>
                    </div>
                    <div>
                        <div style="font-weight:bold; font-size:8px; color:#666;">5. MATERIAIS NECESSÁRIOS:</div>
                        <div style="font-size:9px;">${totals.totalItems} (${totals.rawMaterialItems} MP / ${totals.packagingItems} EMB)</div>
                    </div>
                    <div>
                        <div style="font-weight:bold; font-size:8px; color:#666;">6. CUSTO TOTAL ESTIMADO:</div>
                        <div style="font-size:9px; font-weight:bold; color:#e67e22;">${formatCurrencyBRL(totals.totalCost)}</div>
                    </div>
                </div>

                ${budgetNotes ? `
                    <div style="margin-bottom:12px; padding:8px 12px; background:#fff3cd; border:1px solid #ffe08a; border-radius:4px; font-size:8px;">
                        <strong>OBSERVAÇÕES:</strong> ${budgetNotes}
                    </div>
                ` : ''}

                <!-- SELECTED PRODUCTS -->
                <div style="margin-bottom:12px;">
                    <div style="font-size:10px; font-weight:bold; color:#1a237e; margin-bottom:6px;">PRODUTOS SELECIONADOS</div>
                    <table style="width:100%; border-collapse:collapse; font-size:9px; border:1px solid #ddd;">
                        <thead>
                            <tr style="background:#e8eaf6;">
                                <th style="border:1px solid #ddd; padding:5px; text-align:center; width:35px;">#</th>
                                <th style="border:1px solid #ddd; padding:5px; text-align:left;">CÓDIGO</th>
                                <th style="border:1px solid #ddd; padding:5px; text-align:left;">PRODUTO</th>
                                <th style="border:1px solid #ddd; padding:5px; text-align:center; width:90px;">QUANTIDADE</th>
                                <th style="border:1px solid #ddd; padding:5px; text-align:center; width:60px;">MODO</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${selectedItems.map((it, i) => `
                                <tr>
                                    <td style="border:1px solid #ddd; padding:5px; text-align:center;">${i + 1}</td>
                                    <td style="border:1px solid #ddd; padding:5px; font-weight:bold;">${it.productCode}</td>
                                    <td style="border:1px solid #ddd; padding:5px;">${it.productName}</td>
                                    <td style="border:1px solid #ddd; padding:5px; text-align:center;">${formatNumber(it.quantity, 2)}</td>
                                    <td style="border:1px solid #ddd; padding:5px; text-align:center;">${it.mode === 'mass' ? 'Kg' : 'Un'}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>

                <div style="font-size:10px; font-weight:bold; color:#1a237e; margin-bottom:6px;">MATERIAIS NECESSÁRIOS</div>
            ` : `
                <div style="font-size:10px; font-weight:bold; color:#1a237e; margin-bottom:6px; padding-top:4px;">
                    MATERIAIS NECESSÁRIOS (continuação - página ${pageIndex + 1}/${materialChunks.length})
                </div>
            `}

            <table style="width:100%; border-collapse:collapse; font-size:9px; border:1px solid #ddd; margin-bottom:12px;">
                ${renderTableHead()}
                <tbody>
                    ${chunkHtml}
                    ${isLast ? renderTotalsRows() : ''}
                </tbody>
            </table>

            ${isLast ? `
                <!-- SUPPLIER SUMMARY -->
                <div style="font-size:10px; font-weight:bold; color:#1a237e; margin:8px 0 6px 0;">RESUMO POR FORNECEDOR</div>
                <table style="width:100%; border-collapse:collapse; font-size:9px; border:1px solid #ddd; margin-bottom:12px;">
                    <thead>
                        <tr style="background:#e8eaf6;">
                            <th style="border:1px solid #ddd; padding:5px; text-align:left; width:70px;">CÓDIGO</th>
                            <th style="border:1px solid #ddd; padding:5px; text-align:left;">FORNECEDOR</th>
                            <th style="border:1px solid #ddd; padding:5px; text-align:left;">CONTATO</th>
                            <th style="border:1px solid #ddd; padding:5px; text-align:center; width:60px;">ITENS</th>
                            <th style="border:1px solid #ddd; padding:5px; text-align:right; width:90px;">VALOR TOTAL</th>
                            <th style="border:1px solid #ddd; padding:5px; text-align:center; width:60px;">LEAD</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${supplierList.map(sup => `
                            <tr>
                                <td style="border:1px solid #ddd; padding:5px; font-weight:bold;">${sup.codigo || '—'}</td>
                                <td style="border:1px solid #ddd; padding:5px;">
                                    ${sup.razaoSocial}
                                    ${sup.cnpjCpf ? `<div style="font-size:7px; color:#666;">${sup.cnpjCpf}</div>` : ''}
                                </td>
                                <td style="border:1px solid #ddd; padding:5px; font-size:8px;">
                                    ${sup.contactName ? `<div>${sup.contactName}</div>` : ''}
                                    ${sup.phone ? `<div>${sup.phone}</div>` : ''}
                                    ${sup.email ? `<div style="color:#666;">${sup.email}</div>` : ''}
                                </td>
                                <td style="border:1px solid #ddd; padding:5px; text-align:center;">${sup.items.length}</td>
                                <td style="border:1px solid #ddd; padding:5px; text-align:right; font-weight:bold; color:#e67e22;">${formatCurrencyBRL(sup.total)}</td>
                                <td style="border:1px solid #ddd; padding:5px; text-align:center;">${sup.maxLeadTime ? `${sup.maxLeadTime}d` : '—'}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>

                <!-- PAYMENT/SUMMARY BOX -->
                <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-top:8px; margin-bottom:12px; padding:12px 16px; background:#f0f4ff; border:1px solid #1a237e; border-radius:4px;">
                    <div>
                        <div style="font-size:9px; font-weight:bold; color:#1a237e;">RESUMO DO ORÇAMENTO</div>
                        <div style="font-size:8px; margin-top:4px; line-height:1.6;">
                            <div><strong>Total de Materiais:</strong> ${totals.totalItems}</div>
                            <div><strong>Matérias-Primas:</strong> ${totals.rawMaterialItems}</div>
                            <div><strong>Embalagens:</strong> ${totals.packagingItems}</div>
                        </div>
                    </div>
                    <div style="text-align:right; font-size:8px; color:#666;">
                        <div><strong>Produtos:</strong> ${selectedItems.length}</div>
                        <div style="font-size:10px; margin-top:4px; color:#1a237e; font-weight:bold;">
                            CUSTO TOTAL: ${formatCurrencyBRL(totals.totalCost)}
                        </div>
                    </div>
                </div>

                <!-- SIGNATURES -->
                <div style="display:grid; grid-template-columns:1fr 1fr 1fr; gap:20px; padding-bottom:5px; border-top:1px solid #ccc; margin-top:10px;">
                    <div style="text-align:center;">
                        <div style="font-weight:bold; font-size:8px;">Elaborado por</div>
                        <div style="font-size:9px; color:#666; margin-top:1px; border-top:1px solid #ccc; padding-top:4px;">Data: ${formattedDate}</div>
                    </div>
                    <div style="text-align:center;">
                        <div style="font-weight:bold; font-size:8px;">Revisado por</div>
                        <div style="font-size:9px; color:#666; margin-top:1px; border-top:1px solid #ccc; padding-top:4px;">Data: ${formattedDate}</div>
                    </div>
                    <div style="text-align:center;">
                        <div style="font-weight:bold; font-size:8px;">Aprovado por</div>
                        <div style="font-size:9px; color:#666; margin-top:1px; border-top:1px solid #ccc; padding-top:4px;">Data: ${formattedDate}</div>
                    </div>
                </div>
                <div style="margin-top:8px; text-align:right; font-size:8px; color:#666; font-style:italic;">
                    Documento gerado eletronicamente em: ${formatCurrentDateTime()}
                </div>
            ` : ''}
        </div>
        `;
    };

    const pagesHtml = materialChunks.map((chunk, i) =>
        buildPage(chunk, i, i === 0, i === materialChunks.length - 1)
    ).join('');

    const htmlContent = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>Orçamento de Materiais - ${budgetName || 'Sem título'}</title>
  <style>
    @page { size: A4 portrait; margin: 8mm 12mm 15mm 12mm; }
    * { margin:0; padding:0; box-sizing:border-box; -webkit-print-color-adjust:exact !important; print-color-adjust:exact !important; }
    html, body { background:#fff !important; font-family: Calibri, Arial, sans-serif; font-size:9.5px; color:#000; width:100%; }
    .print-document { width:100%; margin:0 auto; background:#fff !important; }
    .no-print { display:none !important; }
    .page-block { page-break-after: always !important; break-after: page !important; page-break-inside: avoid !important; break-inside: avoid !important; display:block !important; width:100% !important; }
    .page-block:last-child { page-break-after: auto !important; break-after: auto !important; }
    table { page-break-inside: avoid !important; break-inside: avoid !important; }
    thead { display: table-header-group; }
    tr { page-break-inside: avoid !important; break-inside: avoid !important; }
    td, th { page-break-inside: avoid !important; break-inside: avoid !important; }
  </style>
</head>
<body>
  <div class="print-document">
    ${pagesHtml}
  </div>
  <script>
    window.onload = function() {
      window.print();
      setTimeout(function() { window.close(); }, 500);
    };
  </script>
</body>
</html>`;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
}, [materialRequirements, selectedItems, totals, budgetName, budgetNotes, addToast, t]);

    // ============================================================================
    // LOADING STATE
    // ============================================================================
    if (isLoading) {
        return (
            <div className={styles.pageContainer}>
                <PageHeader title={t('pages.materialBudget.title')} subtitle={t('pages.materialBudget.subtitle')} />
                <div className={styles.tableContainer}>
                    <TableSkeleton rows={5} columns={6} />
                </div>
            </div>
        );
    }

    // ============================================================================
    // RENDER
    // ============================================================================
    return (
        <div className={styles.pageContainer}>
            {/* Toast Notifications */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer}>
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => removeToast(toast.id)}
                        />
                    ))}
                </div>
            )}

            {/* Clear Confirmation Modal */}
            {showConfirmClear && (
                <ConfirmModal
                    isOpen={true}
                    onClose={() => setShowConfirmClear(false)}
                    onConfirm={handleClearAll}
                    title={t('pages.materialBudget.clearConfirmTitle')}
                    message={t('pages.materialBudget.clearConfirmMessage')}
                    confirmText={t('common.confirm')}
                    cancelText={t('common.cancel')}
                    danger={true}
                />
            )}

            {/* Page Header */}
            <PageHeader
                title={t('pages.materialBudget.title')}
                subtitle={t('pages.materialBudget.subtitle')}
            />

            {/* Info Banner */}
            <div style={{
                backgroundColor: '#e3f2fd',
                border: '1px solid #90caf9',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '0.9rem',
                color: '#1565c0'
            }}>
                <FiAlertCircle size={20} />
                <span>{t('pages.materialBudget.infoBanner')}</span>
            </div>

            {/* Budget Configuration */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '20px',
                marginBottom: '20px'
            }}>
                <div className={styles.inputGroup}>
                    <label><FiClipboard size={14} style={{ marginRight: '6px' }} /> {t('pages.materialBudget.budgetName')}</label>
                    <input
                        type="text"
                        className={styles.input}
                        value={budgetName}
                        onChange={(e) => setBudgetName(e.target.value)}
                        placeholder={t('pages.materialBudget.budgetNamePlaceholder')}
                    />
                </div>
                <div className={styles.inputGroup}>
                    <label><FiFileText size={14} style={{ marginRight: '6px' }} /> {t('pages.materialBudget.notes')}</label>
                    <input
                        type="text"
                        className={styles.input}
                        value={budgetNotes}
                        onChange={(e) => setBudgetNotes(e.target.value)}
                        placeholder={t('pages.materialBudget.notesPlaceholder')}
                    />
                </div>
            </div>

            {/* Add Product Section */}
            <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
                <h3 style={{ margin: '0 0 15px 0', display: 'flex', alignItems: 'center', gap: '8px', color: '#1a5276' }}>
                    <FiPlus size={20} /> {t('pages.materialBudget.addProducts')}
                </h3>

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ flex: '2', minWidth: '250px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', color: '#666' }}>
                            {t('pages.materialBudget.selectProduct')}
                        </label>
                        <Select
                            options={productOptions}
                            value={selectedProduct}
                            onChange={setSelectedProduct}
                            placeholder={t('pages.materialBudget.selectProductPlaceholder')}
                            isClearable
                            isSearchable
                        />
                    </div>

                    <div style={{ flex: '0 0 140px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', color: '#666' }}>
                            {t('pages.materialBudget.mode')}
                        </label>
                        <select
                            className={styles.select}
                            value={inputMode}
                            onChange={(e) => setInputMode(e.target.value)}
                            style={{ width: '100%' }}
                        >
                            <option value="mass">{t('pages.materialBudget.massMode')}</option>
                            <option value="units">{t('pages.materialBudget.unitsMode')}</option>
                        </select>
                    </div>

                    <div style={{ flex: '0 0 120px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', color: '#666' }}>
                            {t('pages.materialBudget.quantity')}
                        </label>
                        <input
                            type="number"
                            className={styles.input}
                            value={inputQuantity}
                            onChange={(e) => setInputQuantity(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
                            placeholder="0.00"
                            min="0"
                            step="any"
                            style={{ width: '100%' }}
                        />
                    </div>

                    <button
                        onClick={handleAddItem}
                        disabled={!selectedProduct || !inputQuantity}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: selectedProduct && inputQuantity ? '#27ae60' : '#95a5a6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: selectedProduct && inputQuantity ? 'pointer' : 'not-allowed',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontWeight: '500'
                        }}
                    >
                        <FiPlus size={18} /> {t('common.add')}
                    </button>
                </div>

                {/* Selected Items List */}
                {selectedItems.length > 0 && (
                    <div style={{ marginTop: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <h4 style={{ margin: 0, color: '#1a5276' }}>
                                {t('pages.materialBudget.selectedProducts')} ({selectedItems.length})
                            </h4>
                            <button
                                onClick={() => setShowConfirmClear(true)}
                                style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#e74c3c',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                }}
                            >
                                <FiTrash2 size={14} /> {t('common.clearAll')}
                            </button>
                        </div>

                        <div className={styles.tableContainer}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>{t('pages.materialBudget.table.code')}</th>
                                        <th>{t('pages.materialBudget.table.product')}</th>
                                        <th>{t('pages.materialBudget.table.quantity')}</th>
                                        <th>{t('pages.materialBudget.table.mode')}</th>
                                        <th style={{ width: '80px' }}>{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedItems.map(item => (
                                        <tr key={item.tempId}>
                                            <td><strong>{item.productCode}</strong></td>
                                            <td>{item.productName}</td>
                                            <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{item.quantity}</td>
                                            <td style={{ textAlign: 'center' }}>
                                                {item.mode === 'mass' ? 'Kg' : 'Un'}
                                            </td>
                                            <td>
                                                <button
                                                    onClick={() => handleRemoveItem(item.tempId)}
                                                    style={{
                                                        padding: '4px 8px',
                                                        backgroundColor: '#e74c3c',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer'
                                                    }}
                                                    title={t('common.remove')}
                                                >
                                                    <FiTrash2 size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* Results Section */}
            {selectedItems.length > 0 && materialRequirements.length > 0 && (
                <>
                    {/* Summary Cards */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: '15px',
                        marginBottom: '20px'
                    }}>
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            padding: '20px',
                            textAlign: 'center',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            borderTop: '4px solid #3498db'
                        }}>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3498db' }}>
                                {totals.totalItems}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                                {t('pages.materialBudget.totalMaterials')}
                            </div>
                        </div>

                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            padding: '20px',
                            textAlign: 'center',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            borderTop: '4px solid #27ae60'
                        }}>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#27ae60' }}>
                                {totals.rawMaterialItems}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                                {t('pages.materialBudget.rawMaterials')}
                            </div>
                        </div>

                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            padding: '20px',
                            textAlign: 'center',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            borderTop: '4px solid #9b59b6'
                        }}>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#9b59b6' }}>
                                {totals.packagingItems}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                                {t('pages.materialBudget.packaging')}
                            </div>
                        </div>

                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            padding: '20px',
                            textAlign: 'center',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            borderTop: '4px solid #e67e22'
                        }}>
                            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#e67e22' }}>
                                {new Intl.NumberFormat('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                    minimumFractionDigits: 2
                                }).format(totals.totalCost)}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                                {t('pages.materialBudget.estimatedCost')}
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                        <button
                            onClick={handleExportExcel}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: '#217346',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontWeight: '500',
                                fontSize: '0.95rem'
                            }}
                        >
                            <FaFileExcel size={18} /> {t('pages.materialBudget.exportExcel')}
                        </button>

                        <button
                            onClick={handlePrint}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: '#e74c3c',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontWeight: '500',
                                fontSize: '0.95rem'
                            }}
                        >
                            <FiPrinter size={18} /> {t('pages.materialBudget.printReport')}
                        </button>
                    </div>

                    {/* Materials Table with expandable rows */}
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '20px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        marginBottom: '20px'
                    }}>
                        <h3 style={{ margin: '0 0 15px 0', display: 'flex', alignItems: 'center', gap: '8px', color: '#1a5276' }}>
                            <FiPackage size={20} /> {t('pages.materialBudget.materialRequirements')}
                        </h3>

                        <div className={styles.tableContainer}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '30px' }}></th>
                                        <th>#</th>
                                        <th>{t('pages.materialBudget.table.code')}</th>
                                        <th>{t('pages.materialBudget.table.material')}</th>
                                        <th>{t('pages.materialBudget.table.ncm')}</th>
                                        <th>{t('pages.materialBudget.table.type')}</th>
                                        <th style={{ textAlign: 'right' }}>{t('pages.materialBudget.table.quantity')}</th>
                                        <th>{t('pages.materialBudget.table.unit')}</th>
                                        <th style={{ textAlign: 'right' }}>{t('pages.materialBudget.table.unitPrice')}</th>
                                        <th style={{ textAlign: 'right' }}>{t('pages.materialBudget.table.totalCost')}</th>
                                        <th>{t('pages.materialBudget.table.suppliers')}</th>
                                        <th>{t('pages.materialBudget.table.leadTime')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {materialRequirements.map((req, index) => {
                                        const isExpanded = expandedMaterials[req.materialId];
                                        const showAll = showAllSuppliers[req.materialId];
                                        const displayedSuppliers = showAll ? req.suppliers : req.suppliers.slice(0, 1);

                                        return (
                                            <React.Fragment key={req.materialId}>
                                                <tr style={{ backgroundColor: isExpanded ? '#f0f8ff' : 'transparent' }}>
                                                    <td style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleMaterialExpanded(req.materialId)}>
                                                        {isExpanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                                    </td>
                                                    <td>{index + 1}</td>
                                                    <td><strong>{req.materialCode}</strong></td>
                                                    <td>
                                                        {req.materialName}
                                                        {req.inciName && req.inciName !== req.materialName && (
                                                            <div style={{ fontSize: '0.75rem', color: '#888' }}>{req.inciName}</div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {req.ncmCode ? (
                                                            <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                                {req.ncmCode}
                                                            </span>
                                                        ) : (
                                                            <span style={{ color: '#aaa' }}>—</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span style={{
                                                            padding: '2px 8px',
                                                            borderRadius: '12px',
                                                            fontSize: '0.75rem',
                                                            backgroundColor: req.isPackaging ? '#e3f2fd' : '#f3e5f5',
                                                            color: req.isPackaging ? '#1565c0' : '#7b1fa2'
                                                        }}>
                                                            {req.isPackaging ? t('pages.materialBudget.packagingType') : t('pages.materialBudget.rawType')}
                                                        </span>
                                                    </td>
                                                    <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#1a5276' }}>
                                                        {req.totalNeeded.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 4 })}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>{req.unit}</td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        {new Intl.NumberFormat('pt-BR', {
                                                            style: 'currency',
                                                            currency: 'BRL',
                                                            minimumFractionDigits: 2
                                                        }).format(req.latestPrice)}
                                                    </td>
                                                    <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#e67e22' }}>
                                                        {new Intl.NumberFormat('pt-BR', {
                                                            style: 'currency',
                                                            currency: 'BRL',
                                                            minimumFractionDigits: 2
                                                        }).format(req.totalNeeded * req.latestPrice)}
                                                    </td>
                                                    <td>
                                                        <span style={{ fontSize: '0.85rem' }}>
                                                            {req.latestSupplier}
                                                        </span>
                                                        {req.suppliersCount > 1 && (
                                                            <button
                                                                onClick={() => toggleAllSuppliers(req.materialId)}
                                                                style={{
                                                                    marginLeft: '6px',
                                                                    padding: '2px 6px',
                                                                    backgroundColor: '#3498db',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '10px',
                                                                    cursor: 'pointer',
                                                                    fontSize: '0.7rem'
                                                                }}
                                                                title={t('pages.materialBudget.showAllSuppliers')}
                                                            >
                                                                +{req.suppliersCount - 1}
                                                            </button>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {req.preferredSupplier?.leadTime
                                                            ? `${req.preferredSupplier.leadTime} ${t('pages.materialBudget.days')}`
                                                            : '-'
                                                        }
                                                    </td>
                                                </tr>

                                                {/* Expanded: Product Usage */}
                                                {isExpanded && (
                                                    <tr>
                                                        <td colSpan="12" style={{ padding: '0' }}>
                                                            <div style={{ padding: '12px 20px', backgroundColor: '#f8f9fa', borderTop: '1px solid #e9ecef' }}>
                                                                {/* Product Usage Section */}
                                                                <h5 style={{ margin: '0 0 10px 0', color: '#1a5276', fontSize: '0.9rem' }}>
                                                                    <FiLayers size={14} style={{ marginRight: '6px' }} />
                                                                    {t('pages.materialBudget.usedInProducts')}
                                                                </h5>
                                                                <table style={{ width: '100%', fontSize: '0.85rem', marginBottom: '15px' }}>
                                                                    <thead>
                                                                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                                                            <th style={{ padding: '6px', textAlign: 'left' }}>{t('pages.materialBudget.table.product')}</th>
                                                                            <th style={{ padding: '6px', textAlign: 'right' }}>{t('pages.materialBudget.table.productQty')}</th>
                                                                            <th style={{ padding: '6px', textAlign: 'center' }}>{t('pages.materialBudget.table.formulaPercent')}</th>
                                                                            <th style={{ padding: '6px', textAlign: 'right' }}>{t('pages.materialBudget.table.materialQty')}</th>
                                                                            <th style={{ padding: '6px', textAlign: 'center' }}>{t('pages.materialBudget.table.unit')}</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {req.usedInProducts.map((p, idx) => (
                                                                            <tr key={idx} style={{ borderBottom: '1px solid #f1f1f1' }}>
                                                                                <td style={{ padding: '6px' }}>{p.productCode} - {p.productName}</td>
                                                                                <td style={{ padding: '6px', textAlign: 'right' }}>
                                                                                    {p.productQuantity} {p.productMode === 'mass' ? 'Kg' : 'Un'}
                                                                                </td>
                                                                                <td style={{ padding: '6px', textAlign: 'center' }}>
                                                                                    {req.isPackaging ? '—' : `${p.formulaPercentage}%`}
                                                                                </td>
                                                                                <td style={{ padding: '6px', textAlign: 'right', fontWeight: 'bold' }}>
                                                                                    {p.quantity.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 4 })}
                                                                                </td>
                                                                                <td style={{ padding: '6px', textAlign: 'center' }}>{req.unit}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>

                                                                {/* Suppliers Section */}
                                                                <h5 style={{ margin: '0 0 10px 0', color: '#1a5276', fontSize: '0.9rem' }}>
                                                                    <FiTruck size={14} style={{ marginRight: '6px' }} />
                                                                    {t('pages.materialBudget.allSuppliers')} ({req.suppliersCount})
                                                                </h5>
                                                                {req.suppliers.length > 0 ? (
                                                                    <table style={{ width: '100%', fontSize: '0.85rem' }}>
                                                                        <thead>
                                                                            <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                                                                <th style={{ padding: '6px', textAlign: 'left' }}>{t('pages.materialBudget.table.supplierCode')}</th>
                                                                                <th style={{ padding: '6px', textAlign: 'left' }}>{t('pages.materialBudget.table.supplierName')}</th>
                                                                                <th style={{ padding: '6px', textAlign: 'left' }}>{t('pages.materialBudget.table.cnpjCpf')}</th>
                                                                                <th style={{ padding: '6px', textAlign: 'left' }}>{t('pages.materialBudget.table.contact')}</th>
                                                                                <th style={{ padding: '6px', textAlign: 'right' }}>{t('pages.materialBudget.table.supplierPrice')}</th>
                                                                                <th style={{ padding: '6px', textAlign: 'center' }}>{t('pages.materialBudget.table.supplierUnit')}</th>
                                                                                <th style={{ padding: '6px', textAlign: 'center' }}>{t('pages.materialBudget.table.leadTime')}</th>
                                                                                <th style={{ padding: '6px', textAlign: 'center' }}>{t('pages.materialBudget.table.loteMinimo')}</th>
                                                                                <th style={{ padding: '6px', textAlign: 'center' }}>{t('pages.materialBudget.table.date')}</th>
                                                                                <th style={{ padding: '6px', textAlign: 'center' }}>{t('pages.materialBudget.table.preferred')}</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {req.suppliers.map((sup, idx) => (
                                                                                <tr key={idx} style={{ 
                                                                                    borderBottom: '1px solid #f1f1f1',
                                                                                    backgroundColor: req.preferredSupplier?.supplierId === sup.supplierId ? '#fffef0' : 'transparent'
                                                                                }}>
                                                                                    <td style={{ padding: '6px' }}>{sup.codigo}</td>
                                                                                    <td style={{ padding: '6px', fontWeight: '500' }}>{sup.razaoSocial}</td>
                                                                                    <td style={{ padding: '6px', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                                                        {sup.cnpjCpf || '—'}
                                                                                    </td>
                                                                                    <td style={{ padding: '6px', fontSize: '0.8rem' }}>
                                                                                        {sup.contactName && <div>{sup.contactName}</div>}
                                                                                        {sup.phone && <div>{sup.phone}</div>}
                                                                                        {sup.email && <div style={{ color: '#666' }}>{sup.email}</div>}
                                                                                    </td>
                                                                                    <td style={{ padding: '6px', textAlign: 'right' }}>
                                                                                        {new Intl.NumberFormat('pt-BR', {
                                                                                            style: 'currency',
                                                                                            currency: 'BRL',
                                                                                            minimumFractionDigits: 2
                                                                                        }).format(sup.unitPrice)}
                                                                                    </td>
                                                                                    <td style={{ padding: '6px', textAlign: 'center' }}>{sup.unitOfMeasureName || '—'}</td>
                                                                                    <td style={{ padding: '6px', textAlign: 'center' }}>
                                                                                        {sup.leadTime > 0 ? `${sup.leadTime} ${t('pages.materialBudget.days')}` : '—'}
                                                                                    </td>
                                                                                    <td style={{ padding: '6px', textAlign: 'center' }}>
                                                                                        {sup.loteMinimo > 0 ? `${sup.loteMinimo} ${req.unit}` : '—'}
                                                                                    </td>
                                                                                    <td style={{ padding: '6px', textAlign: 'center', fontSize: '0.8rem' }}>
                                                                                        {sup.data || '—'}
                                                                                    </td>
                                                                                    <td style={{ padding: '6px', textAlign: 'center' }}>
                                                                                        {req.preferredSupplier?.supplierId === sup.supplierId ? (
                                                                                            <span style={{ color: '#27ae60', fontWeight: 'bold' }}>⭐</span>
                                                                                        ) : '—'}
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                ) : (
                                                                    <p style={{ color: '#888', fontStyle: 'italic' }}>
                                                                        {t('pages.materialBudget.noSuppliersForMaterial')}
                                                                    </p>
                                                                )}

                                                                {/* Notes */}
                                                                {req.suppliers.some(s => s.descricao) && (
                                                                    <div style={{ marginTop: '10px', fontSize: '0.8rem', color: '#666' }}>
                                                                        <strong>{t('pages.materialBudget.supplierNotes')}:</strong>
                                                                        <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
                                                                            {req.suppliers.filter(s => s.descricao).map((s, idx) => (
                                                                                <li key={idx}>{s.razaoSocial}: {s.descricao}</li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr style={{ backgroundColor: '#1a5276', color: 'white', fontWeight: 'bold' }}>
                                        <td colSpan="9" style={{ textAlign: 'right', padding: '12px' }}>
                                            {t('pages.materialBudget.grandTotal')}:
                                        </td>
                                        <td style={{ textAlign: 'right', padding: '12px' }}>
                                            {new Intl.NumberFormat('pt-BR', {
                                                style: 'currency',
                                                currency: 'BRL',
                                                minimumFractionDigits: 2
                                            }).format(totals.totalCost)}
                                        </td>
                                        <td colSpan="2"></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>

                    {/* Suppliers Summary - showing ALL suppliers */}
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '20px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                        <h3 style={{ margin: '0 0 15px 0', display: 'flex', alignItems: 'center', gap: '8px', color: '#1a5276' }}>
                            <FiTruck size={20} /> {t('pages.materialBudget.supplierSummary')}
                        </h3>

                        {(() => {
                            const supplierMap = {};
                            materialRequirements.forEach(req => {
                                // Process ALL suppliers, not just preferred
                                req.suppliers.forEach(sup => {
                                    if (!supplierMap[sup.supplierId]) {
                                        supplierMap[sup.supplierId] = {
                                            supplierId: sup.supplierId,
                                            razaoSocial: sup.razaoSocial,
                                            codigo: sup.codigo,
                                            cnpjCpf: sup.cnpjCpf,
                                            email: sup.email || sup.contactEmail,
                                            phone: sup.phone,
                                            contactName: sup.contactName,
                                            materials: [],
                                            total: 0,
                                            maxLeadTime: 0,
                                            preferredCount: 0
                                        };
                                    }
                                    supplierMap[sup.supplierId].materials.push({
                                        ...req,
                                        supplierPrice: sup.unitPrice,
                                        isPreferred: req.preferredSupplier?.supplierId === sup.supplierId
                                    });
                                    supplierMap[sup.supplierId].total += req.totalNeeded * sup.unitPrice;
                                    supplierMap[sup.supplierId].maxLeadTime = Math.max(
                                        supplierMap[sup.supplierId].maxLeadTime,
                                        sup.leadTime || 0
                                    );
                                    if (req.preferredSupplier?.supplierId === sup.supplierId) {
                                        supplierMap[sup.supplierId].preferredCount++;
                                    }
                                });
                            });

                            const supplierList = Object.values(supplierMap)
                                .sort((a, b) => b.total - a.total);

                            if (supplierList.length === 0) {
                                return (
                                    <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>
                                        {t('pages.materialBudget.noSuppliersFound')}
                                    </p>
                                );
                            }

                            return (
                                <div className={styles.tableContainer}>
                                    <table className={styles.table}>
                                        <thead>
                                            <tr>
                                                <th>{t('pages.materialBudget.table.supplierCode')}</th>
                                                <th>{t('pages.materialBudget.table.supplierName')}</th>
                                                <th>{t('pages.materialBudget.table.cnpjCpf')}</th>
                                                <th>{t('pages.materialBudget.table.contact')}</th>
                                                <th>{t('pages.materialBudget.table.materialsCount')}</th>
                                                <th>{t('pages.materialBudget.table.preferredCount')}</th>
                                                <th>{t('pages.materialBudget.table.items')}</th>
                                                <th style={{ textAlign: 'right' }}>{t('pages.materialBudget.table.totalValue')}</th>
                                                <th>{t('pages.materialBudget.table.maxLeadTime')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {supplierList.map(sup => (
                                                <tr key={sup.supplierId}>
                                                    <td><strong>{sup.codigo}</strong></td>
                                                    <td>{sup.razaoSocial}</td>
                                                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                        {sup.cnpjCpf || '—'}
                                                    </td>
                                                    <td style={{ fontSize: '0.8rem' }}>
                                                        {sup.contactName && <div>{sup.contactName}</div>}
                                                        {sup.phone && <div>{sup.phone}</div>}
                                                        {sup.email && <div style={{ color: '#666' }}>{sup.email}</div>}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>{sup.materials.length}</td>
                                                    <td style={{ textAlign: 'center', color: '#27ae60', fontWeight: 'bold' }}>
                                                        {sup.preferredCount}
                                                    </td>
                                                    <td style={{ fontSize: '0.8rem' }}>
                                                        {sup.materials.slice(0, 3).map(m => `${m.materialCode} (${m.totalNeeded.toFixed(2)} ${m.unit})`).join(', ')}
                                                        {sup.materials.length > 3 && ` +${sup.materials.length - 3}`}
                                                    </td>
                                                    <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#e67e22' }}>
                                                        {new Intl.NumberFormat('pt-BR', {
                                                            style: 'currency',
                                                            currency: 'BRL',
                                                            minimumFractionDigits: 2
                                                        }).format(sup.total)}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {sup.maxLeadTime > 0 ? `${sup.maxLeadTime} ${t('pages.materialBudget.days')}` : '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            );
                        })()}
                    </div>
                </>
            )}

            {/* Empty State */}
            {selectedItems.length === 0 && (
                <EmptyState
                    icon="📊"
                    title={t('pages.materialBudget.emptyTitle')}
                    message={t('pages.materialBudget.emptyMessage')}
                />
            )}
        </div>
    );
};

export default MaterialBudgetPage;